import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { makeApiRequest } from '../../../utils/api';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    Box, Grid, Typography, TextField, Button, Alert, Card, CardContent,
    FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText, OutlinedInput,
    FormGroup, FormControlLabel, Divider, ListSubheader
} from '@mui/material';

const assessmentOptions = [
    { label: 'Domain Technical concept', generic: 'DOMAIN TECHNICAL CONCEPT' },
    { label: 'Drafting', generic: 'DRAFTING' },
    { label: 'Modeling', generic: 'MODELING' },
    { label: 'Attendance', generic: 'ATTENDANCE' },
    { label: 'Project', generic: 'PROJECT (DRAFTING + MODELING)' }
];

const AddCourse = () => {
    const navigate = useNavigate();
    const [courseName, setCourseName] = useState('');
    const [modules, setModules] = useState([]);
    const [selectedModuleIds, setSelectedModuleIds] = useState([]);
    const [moduleSearch, setModuleSearch] = useState('');
    const [selectedAssessments, setSelectedAssessments] = useState({});
    const [missingChapters, setMissingChapters] = useState([]);
    const [newChapterNames, setNewChapterNames] = useState({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchModules = async () => {
            try {
                const response = await makeApiRequest('post', '/api/module/list', {
                    token: localStorage.getItem('authToken'),
                    per_page: 1000
                });
                if (response.status === 'success' && response.data) {
                    setModules(response.data.data || []);
                }
            } catch (error) {
                console.error('Error fetching modules:', error);
                toast.error('Failed to load modules.');
            }
        };
        fetchModules();
    }, []);

    const handleModuleChange = (event) => {
        const { target: { value } } = event;
        setSelectedModuleIds(typeof value === 'string' ? value.split(',') : value);
    };

    const filteredModules = modules.filter((m) =>
        (m.module || '').toLowerCase().includes(moduleSearch.toLowerCase())
    );

    const handleAssessmentCheckboxChange = (generic) => {
        setSelectedAssessments(prev => {
            const next = { ...prev };
            if (next[generic]) {
                delete next[generic];
            } else {
                next[generic] = '';
            }
            return next;
        });
    };

    const handleOnscreenNameChange = (generic, value) => {
        setSelectedAssessments(prev => ({
            ...prev,
            [generic]: value
        }));
    };

    const handleAddChapterInline = async (moduleId) => {
        const chapterName = newChapterNames[moduleId];
        if (!chapterName || !chapterName.trim()) {
            toast.warn('Please enter a chapter name');
            return;
        }

        try {
            const response = await makeApiRequest('post', '/api/chapter/add', {
                token: localStorage.getItem('authToken'),
                module_id: moduleId,
                chapter_name: chapterName,
                chapter_status: 1, // active by default
                userid: localStorage.getItem('userid') || localStorage.getItem('user_id'),
            });

            if (response.status === 'success') {
                toast.success('Chapter added successfully!');
                // Remove from missingChapters list
                setMissingChapters(prev => prev.filter(item => item.module_id !== moduleId));
                setNewChapterNames(prev => {
                    const next = { ...prev };
                    delete next[moduleId];
                    return next;
                });
            } else {
                throw new Error('Failed to add chapter');
            }
        } catch (error) {
            console.error('Error adding chapter:', error);
            toast.error(error.response?.data?.message || 'Failed to add chapter');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!courseName.trim()) {
            toast.warn('Please enter a course name');
            return;
        }

        if (selectedModuleIds.length === 0) {
            toast.warn('Please select at least one module');
            return;
        }

        const assessmentKeys = Object.keys(selectedAssessments);
        if (assessmentKeys.length < 3 || assessmentKeys.length > 5) {
            toast.warn('Please select between 3 and 5 assessments');
            return;
        }

        // Validate that onscreen names are filled
        for (const generic of assessmentKeys) {
            if (!selectedAssessments[generic].trim()) {
                toast.warn(`Please enter onscreen name for ${generic}`);
                return;
            }
        }

        const payload = {
            token: localStorage.getItem('authToken'),
            course_name: courseName,
            module_ids: selectedModuleIds,
            assessments: assessmentKeys.map(key => ({
                generic_name: key,
                specific_name: selectedAssessments[key]
            }))
        };

        setLoading(true);
        try {
            const response = await makeApiRequest('post', '/api/course/add', payload);
            if (response.status === 'success') {
                toast.success('Course created successfully!');
                setTimeout(() => navigate('/course'), 1500);
            }
        } catch (error) {
            console.error('Error creating course:', error);
            if (error.response?.data?.errors?.missing_chapters) {
                setMissingChapters(error.response.data.errors.missing_chapters);
                toast.error('Some selected modules do not have any chapters. Please add at least one chapter to continue.');
            } else {
                toast.error(error.response?.data?.message || 'Failed to create course');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ height: 'auto', minHeight: '100vh', backgroundColor: '#ECEDFF', pb: 5 }}>
            <ToastContainer
                position="top-center"
                autoClose={2000}
                hideProgressBar={false}
                closeOnClick
                pauseOnHover={false}
                theme="light"
            />
            <Grid container sx={{ display: 'flex', justifyContent: 'center', pt: 3 }}>
                <Grid item xs={11} md={8}>
                    <Box sx={{ height: '80px', display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
                        <Typography variant="h4" sx={{ color: '#481E82', fontWeight: '500', fontFamily: 'Roboto' }}>
                            Add Course
                        </Typography>
                    </Box>

                    <Card sx={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)', p: 3 }}>
                        <CardContent>
                            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                <TextField
                                    label="Course Name"
                                    required
                                    fullWidth
                                    value={courseName}
                                    onChange={(e) => setCourseName(e.target.value)}
                                    size="small"
                                />

                                <FormControl fullWidth size="small">
                                    <InputLabel id="modules-label">Select Modules</InputLabel>
                                    <Select
                                        labelId="modules-label"
                                        multiple
                                        value={selectedModuleIds}
                                        onChange={handleModuleChange}
                                        onClose={() => setModuleSearch('')}
                                        input={<OutlinedInput label="Select Modules" />}
                                        renderValue={(selected) => selected.map(id => modules.find(m => m.module_id === id)?.module).join(', ')}
                                        MenuProps={{
                                            autoFocus: false,
                                            PaperProps: { style: { maxHeight: 400 } }
                                        }}
                                    >
                                        <ListSubheader sx={{ py: 0 }}>
                                            <TextField
                                                size="small"
                                                fullWidth
                                                autoFocus
                                                placeholder="Search modules..."
                                                value={moduleSearch}
                                                onChange={(e) => setModuleSearch(e.target.value)}
                                                onClick={(e) => e.stopPropagation()}
                                                onKeyDown={(e) => {
                                                    if (e.key !== 'Escape') {
                                                        e.stopPropagation();
                                                    }
                                                }}
                                                sx={{ my: 1 }}
                                            />
                                        </ListSubheader>
                                        {filteredModules.length === 0 ? (
                                            <MenuItem disabled>No modules found</MenuItem>
                                        ) : (
                                            filteredModules.map((m) => (
                                                <MenuItem key={m.module_id} value={m.module_id}>
                                                    <Checkbox checked={selectedModuleIds.indexOf(m.module_id) > -1} />
                                                    <ListItemText primary={m.module} />
                                                </MenuItem>
                                            ))
                                        )}
                                    </Select>
                                </FormControl>

                                <Divider />

                                <Box>
                                    <Typography variant="h6" sx={{ color: '#481E82', mb: 1, fontWeight: '500' }}>
                                        Link Assessments (Select 3 to 5) *
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                        Check the box for each assessment and enter the specific onscreen name as it should appear in the system.
                                    </Typography>

                                    <Grid container spacing={2}>
                                        {assessmentOptions.map((opt) => {
                                            const isSelected = selectedAssessments[opt.generic] !== undefined;
                                            return (
                                                <Grid item xs={12} key={opt.generic} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={isSelected}
                                                                onChange={() => handleAssessmentCheckboxChange(opt.generic)}
                                                            />
                                                        }
                                                        label={opt.label}
                                                        sx={{ width: '250px' }}
                                                    />
                                                    {isSelected && (
                                                        <TextField
                                                            label={`Onscreen Name for ${opt.label}`}
                                                            required
                                                            size="small"
                                                            fullWidth
                                                            value={selectedAssessments[opt.generic]}
                                                            onChange={(e) => handleOnscreenNameChange(opt.generic, e.target.value)}
                                                        />
                                                    )}
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                </Box>

                                {missingChapters.length > 0 && (
                                    <Box sx={{ mt: 2 }}>
                                        <Alert severity="error" sx={{ mb: 2 }}>
                                            The following modules must have at least one chapter before the course can be created. Please add a chapter to resolve:
                                        </Alert>
                                        <Grid container spacing={2}>
                                            {missingChapters.map((m) => (
                                                <Grid item xs={12} key={m.module_id} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Typography sx={{ width: '200px', fontWeight: 'bold' }}>{m.name}</Typography>
                                                    <TextField
                                                        placeholder="Chapter Name"
                                                        size="small"
                                                        value={newChapterNames[m.module_id] || ''}
                                                        onChange={(e) => setNewChapterNames(prev => ({ ...prev, [m.module_id]: e.target.value }))}
                                                    />
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        onClick={() => handleAddChapterInline(m.module_id)}
                                                    >
                                                        Add Chapter
                                                    </Button>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                )}

                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                                    <Button variant="outlined" onClick={() => navigate('/course')}>
                                        Cancel
                                    </Button>
                                    <Button type="submit" variant="contained" disabled={loading} sx={{ backgroundColor: '#481E82', '&:hover': { backgroundColor: '#371566' } }}>
                                        {loading ? 'Creating...' : 'Save Course'}
                                    </Button>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default AddCourse;
